import { Card, Skeleton, Space, Typography, theme as antdTheme } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { useState, type ReactNode } from 'react';

interface Props {
  /** Eyebrow / kicker above the title (small caps). */
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned slot on the header row — actions, filters, toggles. */
  extra?: ReactNode;
  /** When set, the card body collapses on click; `extra` stays clickable. */
  collapsible?: boolean;
  /** Initial open state for collapsible cards. */
  defaultOpen?: boolean;
  /** Loading shell — renders <Skeleton paragraph={rows}/> in body. */
  loading?: boolean;
  rows?: number;
  bordered?: boolean;
  /** Body padding override (Card default is 24). */
  bodyPadding?: number;
  className?: string;
  children?: ReactNode;
}

/**
 * Card with a structured header: eyebrow / title / subtitle / extra. Replaces
 * the "Card + Title + two divs" pattern repeated across the codebase. The
 * collapsible mode is opt-in — once toggled closed, the body unmounts so heavy
 * tables inside don't render needlessly.
 *
 * Loading state pattern:
 *
 *   <SectionCard loading rows={4}>{/* unused while loading * /}</SectionCard>
 *
 * Pair with `SectionCard.Skeleton` for placeholder lists at first paint.
 */
export function SectionCard({
  eyebrow,
  title,
  subtitle,
  extra,
  collapsible = false,
  defaultOpen = true,
  loading = false,
  rows = 3,
  bordered = true,
  bodyPadding,
  className,
  children,
}: Props) {
  const { token } = antdTheme.useToken();
  const [open, setOpen] = useState(defaultOpen);
  const isCollapsed = collapsible && !open;

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      {collapsible && (
        <button
          type="button"
          aria-label={open ? 'Collapse section' : 'Expand section'}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 'none',
            color: token.colorTextSecondary,
            cursor: 'pointer',
            padding: 0,
            fontSize: 14,
            marginTop: 2,
          }}
        >
          {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {eyebrow && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase', display: 'block' }}
          >
            {eyebrow}
          </Typography.Text>
        )}
        {title && (
          <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </div>
        )}
        {subtitle && (
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
            {subtitle}
          </Typography.Text>
        )}
      </div>
      {extra && <Space size={6} style={{ flexShrink: 0 }}>{extra}</Space>}
    </div>
  );

  return (
    <Card
      className={className}
      bordered={bordered}
      styles={{
        body: { padding: bodyPadding ?? (isCollapsed ? 0 : 20) },
      }}
    >
      <div style={{ paddingBottom: isCollapsed ? 0 : 12 }}>{header}</div>
      {!isCollapsed && (
        loading
          ? <Skeleton active paragraph={{ rows }} title={false} />
          : children
      )}
    </Card>
  );
}

/** Placeholder card for first-paint grids. */
SectionCard.Skeleton = function SectionCardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <Card><Skeleton active paragraph={{ rows }} /></Card>
  );
};
